/**
 * Visibility across the five buyer-journey stages, client vs. its best-placed
 * named competitor at each stage (2026-09-08).
 *
 * Lisa's ask (2026-09-01): "definitely by journey stage. So maybe we have like a
 * [chart] for stage one — show how Antibodies Inc does against its named
 * competitor set at stage one. And then we have one for stage two. I'm thinking
 * we need to break it up that way." — and then, a moment later, "is there a
 * journey stage kind of dashboard in itself that kind of aggregates". This is
 * that: one chart carrying all five stages rather than five separate charts, so
 * the SHAPE of the funnel is readable in a glance, which is the thing a
 * leadership reader actually needs.
 *
 * FORM: grouped columns on an ordered axis. The stages have a real order, so the
 * x-axis carries it; the comparison within each stage is two bars side by side.
 * Emphasis colouring — the client in categorical slot 1, the competitor in
 * context grey — because the client's own line is the subject and the
 * competitor is context. The competitor named varies by stage (it is whichever
 * tracked competitor leads there), so it is labelled generically in the legend
 * and named exactly in each tooltip.
 */
import type { StageComparisonRow } from '@aurora/shared';

import { CONTEXT, TARGET as TARGET_HUE } from './palette';

const W = 560;
const H = 190;
const PAD_L = 38;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 46;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;

const pct = (v: number): string => {
  const p = v * 100;
  if (p > 0 && p < 1) return '<1%';
  return `${Math.round(p)}%`;
};

export function StageJourneyChart({
  rows,
  targetName,
}: {
  rows: StageComparisonRow[];
  targetName: string;
}): JSX.Element | null {
  if (rows.length === 0) return null;

  const max = Math.max(
    0.05,
    ...rows.flatMap((r) => [r.targetRate, r.topCompetitor?.rate ?? 0]),
  );
  // Round the axis top up to a clean 5% step so the gridline labels read well.
  const top = Math.ceil((max * 100) / 5) * 5 / 100;

  const slotW = PLOT_W / rows.length;
  const barW = Math.min(26, slotW / 3.2);
  const gap = 5;

  const ticks = [0, top / 2, top];

  return (
    <figure className="stage-journey-wrap">
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMinYMin meet"
        role="img"
        aria-label={`${targetName} visibility by buyer-journey stage, against the leading named competitor at each stage`}
      >
        {ticks.map((t) => {
          const y = PAD_T + PLOT_H - (t / top) * PLOT_H;
          return (
            <g key={t}>
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={y}
                y2={y}
                className="chart-gridline"
              />
              <text
                x={PAD_L - 6}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                className="chart-bar-label"
              >
                {pct(t)}
              </text>
            </g>
          );
        })}

        {rows.map((r, i) => {
          const cx = PAD_L + i * slotW + slotW / 2;
          const th = (r.targetRate / top) * PLOT_H;
          const ch = ((r.topCompetitor?.rate ?? 0) / top) * PLOT_H;
          const xT = cx - barW - gap / 2;
          const xC = cx + gap / 2;
          return (
            <g key={r.stage}>
              <rect
                x={xT}
                y={PAD_T + PLOT_H - th}
                width={barW}
                height={Math.max(r.targetRate > 0 ? 2 : 0, th)}
                rx={3}
                fill={TARGET_HUE}
              >
                <title>
                  {targetName} — {r.label}: {pct(r.targetRate)} visible
                  {r.targetRecommendations > 0
                    ? `, ${r.targetRecommendations} recommendation${r.targetRecommendations === 1 ? '' : 's'}`
                    : ', no recommendations'}
                </title>
              </rect>
              {r.topCompetitor && (
                // Always at least a 2px stub, so a competitor that scored 0%
                // reads as a measured zero rather than as an absent bar. An
                // empty slot would be indistinguishable from missing data.
                <rect
                  x={xC}
                  y={PAD_T + PLOT_H - Math.max(2, ch)}
                  width={barW}
                  height={Math.max(2, ch)}
                  rx={3}
                  fill={CONTEXT}
                >
                  <title>
                    {r.topCompetitor.company} — {r.label}:{' '}
                    {pct(r.topCompetitor.rate)} visible
                  </title>
                </rect>
              )}
              {/* Direct-label the client's own bar only — never every mark. */}
              <text
                x={xT + barW / 2}
                y={PAD_T + PLOT_H - th - 4}
                textAnchor="middle"
                className="chart-bar-value"
              >
                {pct(r.targetRate)}
              </text>
              <text
                x={cx}
                y={H - PAD_B + 16}
                textAnchor="middle"
                className="chart-bar-label"
              >
                {r.label.split(' ')[0]}
              </text>
              <text
                x={cx}
                y={H - PAD_B + 29}
                textAnchor="middle"
                className="chart-bar-label"
              >
                {r.label.split(' ').slice(1).join(' ')}
              </text>
            </g>
          );
        })}
        <line
          x1={PAD_L}
          x2={W - PAD_R}
          y1={PAD_T + PLOT_H}
          y2={PAD_T + PLOT_H}
          className="chart-axis"
        />
      </svg>
      <div className="chart-legend">
        <span className="chart-legend-item">
          <span
            className="chart-legend-swatch"
            style={{ background: TARGET_HUE }}
          />
          {targetName}
        </span>
        <span className="chart-legend-item">
          <span className="chart-legend-swatch" style={{ background: CONTEXT }} />
          Leading named competitor at that stage
        </span>
      </div>
    </figure>
  );
}
